// src/pages/HotelAgentKycPage.jsx
import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
  Divider,
} from "antd";
import {
  CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
  FileTextOutlined, IdcardOutlined, ReloadOutlined, SafetyOutlined,
  UserOutlined, WarningOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { Input } from "antd";
import notify from "../../utils/notify";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ─── helpers ────────────────────────────────────────────────────────────────

const KYC_STATUS_FILTER = [
  { value: "all", label: "All KYC Statuses" },
  { value: "notstarted", label: "Not Started" },
  { value: "submitted", label: "Submitted" },
  { value: "review", label: "Under Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "resubmission", label: "Resubmission Required" },
];

const kycTagColor = (s) => {
  const v = String(s ?? "").toLowerCase();
  if (v.includes("approved") || v === "4") return "green";
  if (v.includes("rejected") || v === "5") return "red";
  if (v.includes("resubmission") || v === "6") return "orange";
  if (v.includes("review") || v === "3" || v.includes("submitted") || v === "2") return "blue";
  return "default";
};

const kycLabel = (s) => {
  const v = String(s ?? "").toLowerCase();
  if (v.includes("approved") || v === "4") return "Approved";
  if (v.includes("rejected") || v === "5") return "Rejected";
  if (v.includes("resubmission") || v === "6") return "Resubmission Required";
  if (v.includes("review") || v === "3") return "Under Review";
  if (v.includes("submitted") || v === "2") return "Submitted";
  if (v === "notstarted" || v === "1") return "Not Started";
  return String(s);
};

const appStatusTagColor = (s) => {
  const v = String(s ?? "").toLowerCase();
  if (v.includes("approved") || v === "3") return "green";
  if (v.includes("rejected") || v === "4") return "red";
  if (v.includes("pending") || v === "1" || v === "2") return "gold";
  return "default";
};

const fmt = (utc) => (utc ? new Date(utc).toLocaleString() : "—");
const fmtDate = (utc) => (utc ? new Date(utc).toLocaleDateString() : "—");

// ─── KYC Steps helper ────────────────────────────────────────────────────────

const getKycStep = (status) => {
  const v = String(status ?? "").toLowerCase();
  if (v.includes("notstarted") || v === "1") return 0;
  if (v.includes("submitted") || v === "2") return 1;
  if (v.includes("review") || v === "3") return 2;
  if (v.includes("approved") || v === "4") return 3;
  if (v.includes("rejected") || v === "5") return 3;
  if (v.includes("resubmission") || v === "6") return 1; // back to submit
  return 0;
};

// ─── component ──────────────────────────────────────────────────────────────

const HotelAgentKycPage = () => {
  const { authData } = useAuth();
  const queryClient = useQueryClient();
  const hotelId = authData?.hotelId;

  const [kycFilter, setKycFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedId(null);
    setReviewNotes("");
  };

  // ── queries ──────────────────────────────────────────────────────────────

  const appsQuery = useQuery({
    queryKey: ["agent-applications-kyc", hotelId],
    enabled: !!hotelId,
    retry: false,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/HotelAgentApplications/hotels/${hotelId}/applications`
      );
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const detailQuery = useQuery({
    queryKey: ["agent-application-kyc-detail", hotelId, selectedId],
    enabled: !!hotelId && !!selectedId && drawerOpen,
    retry: false,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedId}`
      );
      return res.data;
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["agent-applications-kyc", hotelId] });
    if (selectedId) {
      queryClient.invalidateQueries({
        queryKey: ["agent-application-kyc-detail", hotelId, selectedId],
      });
    }
  };

  // ── mutations ─────────────────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: async () =>
      (
        await axiosInstance.post(
          `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedId}/approve`,
          { reviewNotes, commissionPercentage: null }
        )
      ).data,
    onSuccess: () => { notify.success("Application approved."); closeDrawer(); refresh(); },
    onError: (e) => notify.error(e?.response?.data?.message || "Approval failed."),
  });

  const rejectMutation = useMutation({
    mutationFn: async () =>
      (
        await axiosInstance.post(
          `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedId}/reject`,
          { reviewNotes }
        )
      ).data,
    onSuccess: () => { notify.success("KYC rejected."); closeDrawer(); refresh(); },
    onError: (e) => notify.error(e?.response?.data?.message || "Reject failed."),
  });

  const resubmitMutation = useMutation({
    mutationFn: async () =>
      (
        await axiosInstance.post(
          `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedId}/request-resubmission`,
          { reviewNotes }
        )
      ).data,
    onSuccess: () => { notify.success("Resubmission requested."); closeDrawer(); refresh(); },
    onError: (e) => notify.error(e?.response?.data?.message || "Request failed."),
  });

  // ── derived state ─────────────────────────────────────────────────────────

  const applications = useMemo(() => appsQuery.data ?? [], [appsQuery.data]);

  const filtered = useMemo(() => {
    if (kycFilter === "all") return applications;
    return applications.filter((a) => {
      const kyc = String(
        a.latestKycStatus ?? a.latestKyc?.reviewStatus ?? "notstarted"
      ).toLowerCase();
      return kyc.includes(kycFilter);
    });
  }, [applications, kycFilter]);

  const kycCounts = useMemo(() => {
    const counts = { submitted: 0, review: 0, approved: 0, rejected: 0, resubmission: 0, notstarted: 0 };
    applications.forEach((a) => {
      const k = String(a.latestKycStatus ?? a.latestKyc?.reviewStatus ?? "notstarted").toLowerCase();
      if (k.includes("approved") || k === "4") counts.approved++;
      else if (k.includes("rejected") || k === "5") counts.rejected++;
      else if (k.includes("resubmission") || k === "6") counts.resubmission++;
      else if (k.includes("review") || k === "3") counts.review++;
      else if (k.includes("submitted") || k === "2") counts.submitted++;
      else counts.notstarted++;
    });
    return counts;
  }, [applications]);

  const detail = detailQuery.data;
  const latestKyc = detail?.kycSubmissions?.[0] ?? null;
  const kycStatus = String(latestKyc?.reviewStatus ?? "notstarted").toLowerCase();
  const kycStep = getKycStep(kycStatus);
  const canReview =
    kycStatus.includes("submitted") ||
    kycStatus === "2" ||
    kycStatus.includes("review") ||
    kycStatus === "3";

  const openDrawer = (id) => {
    setSelectedId(id);
    setReviewNotes("");
    setDrawerOpen(true);
  };

  // ── columns ───────────────────────────────────────────────────────────────

  const columns = [
    {
      title: "Agent",
      key: "agent",
      render: (_, row) => (
        <Space>
          <Avatar icon={<UserOutlined />} style={{ background: "var(--hms-accent)" }} />
          <div>
            <div style={{ fontWeight: 600 }}>{row.agentName || "—"}</div>
            <div style={{ color: "#8c8c8c", fontSize: 12 }}>{row.companyName || "—"}</div>
          </div>
        </Space>
      ),
    },
    {
      title: "Application Status",
      dataIndex: "status",
      render: (v) => <Tag color={appStatusTagColor(v)}>{String(v)}</Tag>,
    },
    {
      title: "KYC Status",
      key: "kyc",
      render: (_, row) => {
        const kyc = row.latestKycStatus ?? row.latestKyc?.reviewStatus ?? "NotStarted";
        return <Tag color={kycTagColor(kyc)}>{kycLabel(kyc)}</Tag>;
      },
    },
    {
      title: "Submitted",
      key: "submitted",
      render: (_, row) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {row.latestKyc?.submittedAtUtc ? fmtDate(row.latestKyc.submittedAtUtc) : "—"}
        </Text>
      ),
    },
    {
      title: "",
      key: "action",
      render: (_, row) => (
        <Button
          type="primary"
          ghost
          size="small"
          icon={<EyeOutlined />}
          onClick={() => openDrawer(row.hotelAgentApplicationId)}
        >
          View KYC
        </Button>
      ),
    },
  ];

  // ── render ────────────────────────────────────────────────────────────────

  if (!hotelId) return <Alert type="warning" message="No hotel context." showIcon />;

  return (
    <div style={{ padding: "8px 0" }}>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <Title level={3} style={{ marginBottom: 4 }}>KYC Verification</Title>
            <Text type="secondary">
              Review identity and business verification documents submitted by agent applicants.
            </Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
        </div>

        {/* KYC status overview */}
        <Row gutter={[12, 12]}>
          {[
            { key: "submitted", label: "Submitted", color: "var(--hms-accent)", bg: "var(--hms-wash)", border: "var(--hms-accent)" },
            { key: "review", label: "Under Review", color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7" },
            { key: "approved", label: "Approved", color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f" },
            { key: "rejected", label: "Rejected", color: "#ff4d4f", bg: "#fff2f0", border: "#ffccc7" },
            { key: "resubmission", label: "Needs Resubmit", color: "#fa8c16", bg: "#fff7e6", border: "#ffd591" },
          ].map((s) => (
            <Col xs={12} sm={12} lg={4} key={s.key}>
              <Card
                style={{
                  background: s.bg,
                  border: `1px solid ${s.border}`,
                  borderRadius: 10,
                  cursor: "pointer",
                  outline: kycFilter === s.key ? `2px solid ${s.color}` : "none",
                }}
                bodyStyle={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}
                onClick={() => setKycFilter(kycFilter === s.key ? "all" : s.key)}
              >
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>
                  {kycCounts[s.key] ?? 0}
                </div>
                <div style={{ fontSize: 12, color: "#595959" }}>{s.label}</div>
              </Card>
            </Col>
          ))}
          <Col xs={12} sm={12} lg={4}>
            <Card
              style={{ borderRadius: 10, cursor: "pointer", outline: kycFilter === "all" ? "2px solid var(--hms-accent)" : "none" }}
              bodyStyle={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}
              onClick={() => setKycFilter("all")}
            >
              <div style={{ fontSize: 22, fontWeight: 700, color: "#595959" }}>{applications.length}</div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>All</div>
            </Card>
          </Col>
        </Row>

        {/* Alerts */}
        {(kycCounts.submitted + kycCounts.review) > 0 && (
          <Alert
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
            message={
              <Text>
                <Text strong>{kycCounts.submitted + kycCounts.review}</Text> agent
                {kycCounts.submitted + kycCounts.review !== 1 ? "s" : ""} have KYC documents awaiting your review.
              </Text>
            }
          />
        )}
        {appsQuery.isError && (
          <Alert type="warning" showIcon message="Could not load applications." />
        )}

        {/* Filter bar */}
        <Card bodyStyle={{ padding: "12px 16px" }} style={{ borderRadius: 10 }}>
          <Row gutter={[12, 8]} align="middle">
            <Col xs={24} sm={10}>
              <Select
                style={{ width: "100%" }}
                value={kycFilter}
                onChange={setKycFilter}
                options={KYC_STATUS_FILTER}
              />
            </Col>
            <Col>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Showing {filtered.length} of {applications.length}
              </Text>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Card style={{ borderRadius: 10 }}>
          <Table
            rowKey="hotelAgentApplicationId"
            columns={columns}
            dataSource={filtered}
            loading={appsQuery.isLoading}
            pagination={{ pageSize: 12 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No results." /> }}
          />
        </Card>
      </Space>

      {/* ── KYC Detail Drawer ── */}
      <Drawer
        title={
          <Space>
            <SafetyOutlined />
            KYC Review
            {latestKyc && (
              <Tag color={kycTagColor(latestKyc.reviewStatus)}>
                {kycLabel(latestKyc.reviewStatus)}
              </Tag>
            )}
          </Space>
        }
        width={660}
        open={drawerOpen}
        onClose={closeDrawer}
      >
        {detailQuery.isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <Spin />
          </div>
        ) : detail ? (
          <Space direction="vertical" size={14} style={{ width: "100%" }}>

            {/* Agent summary */}
            <Card
              size="small"
              style={{ background: "#fafafa", borderRadius: 10 }}
              bodyStyle={{ display: "flex", alignItems: "center", gap: 14 }}
            >
              <Avatar size={48} icon={<UserOutlined />} style={{ background: "var(--hms-accent)" }} />
              <div>
                <Text strong style={{ fontSize: 15 }}>{detail.agent?.displayName || "—"}</Text>
                <br />
                <Text type="secondary">{detail.agent?.companyName || "—"}</Text>
                <br />
                <Tag color={appStatusTagColor(detail.status)} style={{ marginTop: 4 }}>
                  Application: {String(detail.status)}
                </Tag>
              </div>
            </Card>

            {/* KYC Progress steps */}
            <Card size="small" title="KYC Progress" style={{ borderRadius: 10 }}>
              <Steps
                size="small"
                current={kycStep}
                status={
                  kycStatus.includes("rejected") || kycStatus === "5" ? "error" :
                  kycStatus.includes("approved") || kycStatus === "4" ? "finish" : "process"
                }
                items={[
                  { title: "Not Started" },
                  { title: "Submitted" },
                  { title: "Under Review" },
                  {
                    title: kycStatus.includes("rejected") || kycStatus === "5" ? "Rejected" : "Approved",
                  },
                ]}
              />
            </Card>

            {/* KYC submissions (all, newest first) */}
            {detail.kycSubmissions?.length ? (
              detail.kycSubmissions.map((kyc, idx) => (
                <Card
                  key={kyc.agentKycSubmissionId}
                  size="small"
                  style={{
                    borderRadius: 10,
                    border: `1px solid ${
                      kycTagColor(kyc.reviewStatus) === "green" ? "#b7eb8f" :
                      kycTagColor(kyc.reviewStatus) === "red" ? "#ffccc7" : "#d9d9d9"
                    }`,
                  }}
                  title={
                    <Space>
                      <IdcardOutlined />
                      <Text strong>{idx === 0 ? "Latest Submission" : `Submission #${kyc.agentKycSubmissionId}`}</Text>
                      <Tag color={kycTagColor(kyc.reviewStatus)}>{kycLabel(kyc.reviewStatus)}</Tag>
                    </Space>
                  }
                  extra={<Text type="secondary" style={{ fontSize: 12 }}>Submitted {fmtDate(kyc.submittedAtUtc)}</Text>}
                >
                  <Descriptions column={1} bordered size="small" style={{ marginBottom: 12 }}>
                    <Descriptions.Item label="Identity Number">
                      {kyc.identityNumber || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Business Reg. No.">
                      {kyc.businessRegistrationNumber || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tax Number">
                      {kyc.taxNumber || "—"}
                    </Descriptions.Item>
                    {kyc.reviewedAtUtc && (
                      <Descriptions.Item label="Reviewed">
                        {fmt(kyc.reviewedAtUtc)}
                      </Descriptions.Item>
                    )}
                    {kyc.reviewNotes && (
                      <Descriptions.Item label="Review Notes">
                        {kyc.reviewNotes}
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  {/* Documents */}
                  {kyc.documents?.length > 0 && (
                    <div>
                      <Text strong style={{ display: "block", marginBottom: 8 }}>Documents</Text>
                      <Row gutter={[8, 8]}>
                        {kyc.documents.map((doc) => (
                          <Col xs={24} sm={12} key={doc.agentKycDocumentId}>
                            <a href={doc.filePath} target="_blank" rel="noreferrer">
                              <Card
                                size="small"
                                hoverable
                                style={{ borderRadius: 8 }}
                                bodyStyle={{ display: "flex", alignItems: "center", gap: 8 }}
                              >
                                <FileTextOutlined style={{ color: "var(--hms-accent)", fontSize: 20 }} />
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: 13 }}>{doc.documentType}</div>
                                  <div style={{ color: "#8c8c8c", fontSize: 11 }}>{doc.fileName}</div>
                                </div>
                              </Card>
                            </a>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </Card>
              ))
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No KYC submissions yet." />
            )}

            {/* Review action */}
            <Card
              size="small"
              title={<Space><CheckCircleOutlined />{canReview ? "Make a KYC Decision" : "KYC Decision"}</Space>}
              style={{
                borderRadius: 10,
                background: canReview ? "var(--hms-wash)" : "#fafafa",
                border: canReview ? "1px solid var(--hms-accent)" : "1px solid #f0f0f0",
              }}
            >
              {!latestKyc && (
                <Alert type="info" showIcon message="No KYC submitted yet. Awaiting agent action." />
              )}
              {latestKyc && !canReview && (
                <Alert
                  type={
                    kycStatus.includes("approved") || kycStatus === "4" ? "success" :
                    kycStatus.includes("rejected") || kycStatus === "5" ? "error" : "warning"
                  }
                  showIcon
                  message={`KYC is currently: ${kycLabel(latestKyc.reviewStatus)}`}
                />
              )}
              {canReview && (
                <Space direction="vertical" size={10} style={{ width: "100%" }}>
                  <div>
                    <Text strong>Notes for Agent</Text>
                    <TextArea
                      rows={3}
                      style={{ marginTop: 6 }}
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Reason for decision, what needs correction, etc."
                    />
                  </div>
                  <Divider style={{ margin: "4px 0" }} />
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={8}>
                      <Button
                        type="primary" block icon={<CheckCircleOutlined />}
                        loading={approveMutation.isPending}
                        onClick={() => approveMutation.mutate()}
                      >
                        Approve KYC
                      </Button>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Button
                        danger block icon={<CloseCircleOutlined />}
                        loading={rejectMutation.isPending}
                        onClick={() => rejectMutation.mutate()}
                      >
                        Reject KYC
                      </Button>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Button
                        block icon={<WarningOutlined />}
                        loading={resubmitMutation.isPending}
                        onClick={() => resubmitMutation.mutate()}
                      >
                        Request Resubmit
                      </Button>
                    </Col>
                  </Row>
                </Space>
              )}
            </Card>
          </Space>
        ) : (
          <Empty description="No application found." />
        )}
      </Drawer>
    </div>
  );
};

export default HotelAgentKycPage;